import { MapPin, Phone, Mail, Globe, Navigation } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";
import { useLang } from "@/i18n";

const MAP_SRC =
    "https://maps.google.com/maps?q=Signita%20Wellness%2C%20Thane%20West%20400615&z=16&output=embed";
const DIRECTIONS_URL =
    "https://www.google.com/maps/search/?api=1&query=Signita+Wellness+Thane+West+400615";

export default function Location() {
    const { t } = useLang();

    return (
        <section id="location" className="relative bg-brand-olivelight/60 py-24 md:py-32">
            <div className="mx-auto grid max-w-7xl grid-cols-1 items-stretch gap-12 px-5 md:px-8 lg:grid-cols-12">
                <div className="flex flex-col justify-center lg:col-span-5">
                    <Reveal>
                        <Eyebrow>{t.loc.eyebrow}</Eyebrow>
                        <h2 className="mt-4 font-display text-4xl font-semibold leading-[1.08] tracking-tight text-brand-navy md:text-5xl">
                            {t.loc.h2a} <em className="italic text-brand-olive">{t.loc.h2b}</em>
                        </h2>
                    </Reveal>

                    <Reveal delay={0.1} className="mt-10 space-y-5">
                        <div className="flex items-start gap-4">
                            <span className="mt-0.5 flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white text-brand-olive shadow-sm">
                                <MapPin className="h-5 w-5" />
                            </span>
                            <p className="text-sm leading-relaxed text-slate-600 md:text-base">
                                <span className="font-bold text-brand-navy">Signita Wellness</span>
                                <br />
                                Near Oscar Hospital, Kasarvadavali,
                                <br />
                                Ghodbunder Road, Thane West 400615
                            </p>
                        </div>

                        <div className="flex items-center gap-4">
                            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white text-brand-olive shadow-sm">
                                <Phone className="h-5 w-5" />
                            </span>
                            <p className="flex flex-wrap gap-x-4 font-mono text-sm font-semibold text-brand-navy md:text-base">
                                <a data-testid="location-call-1" href="tel:+917208991091" className="transition-colors hover:text-brand-olive">
                                    7208991091
                                </a>
                                <a data-testid="location-call-2" href="tel:+917208991092" className="transition-colors hover:text-brand-olive">
                                    7208991092
                                </a>
                            </p>
                        </div>

                        <div className="flex items-center gap-4">
                            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white text-brand-olive shadow-sm">
                                <Mail className="h-5 w-5" />
                            </span>
                            <a
                                data-testid="location-email-link"
                                href="mailto:hello@signitawellness.in"
                                className="font-mono text-sm text-brand-navy transition-colors hover:text-brand-olive md:text-base"
                            >
                                hello@signitawellness.in
                            </a>
                        </div>

                        <div className="flex items-center gap-4">
                            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white text-brand-olive shadow-sm">
                                <Globe className="h-5 w-5" />
                            </span>
                            <p className="font-mono text-sm text-brand-navy md:text-base">www.signitawellness.in</p>
                        </div>
                    </Reveal>

                    <Reveal delay={0.2} className="mt-10">
                        <a
                            data-testid="location-directions-link"
                            href={DIRECTIONS_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-3 rounded-full bg-brand-navy px-8 py-4 text-sm font-semibold text-white transition-all duration-300 hover:bg-brand-olive"
                        >
                            <Navigation className="h-4 w-4" />
                            {t.loc.directions}
                        </a>
                    </Reveal>
                </div>

                <Reveal delay={0.15} className="lg:col-span-7">
                    <div
                        data-testid="location-map-frame"
                        className="h-[380px] overflow-hidden rounded-[2rem] border border-brand-accentline shadow-lift md:h-full md:min-h-[480px]"
                    >
                        <iframe
                            title="Signita Wellness location — Near Oscar Hospital, Kasarvadavali, Ghodbunder Road, Thane West"
                            src={MAP_SRC}
                            loading="lazy"
                            allowFullScreen
                            referrerPolicy="no-referrer-when-downgrade"
                            className="h-full w-full border-0"
                        />
                    </div>
                </Reveal>
            </div>
        </section>
    );
}
